// Zamanlayici: planli taslaklar (ListingDraft status=scheduled). Yayin kuyrugu
// (publish-queue.service) zamani gelen taslagi Etsy'ye basar. Eski ScheduledListing
// kayitlari hic otomatik yayinlanmiyordu; yalniz okunur gosterilir ve silinebilir.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Clock, Loader2, AlertTriangle, X, Trash2, Calendar } from "lucide-react";
import { authHeaders, getBackendUrl } from "../lib/http";

interface ScheduledDraft {
  id: string;
  title: string;
  scheduledAt: string | null;
  etsyListingId: string | null;
  publishNote: string | null;
  imageUrls: string[];
  sourceImageUrl: string | null;
}

interface Deadline { eventTitle: string; startsAt: string | null; deadline: string | null }

interface LegacyScheduled { id: string; title: string; scheduledAt: string; status: string }

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${getBackendUrl()}${path}`, { ...init, headers: authHeaders() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error?.message || body.error || "İşlem başarısız");
  return body.data ?? body;
}

const fmt = (iso: string) => new Date(iso).toLocaleString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
const fmtDay = (iso: string) => new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
// <input type="datetime-local"> yerel saat bekler
const toLocalInput = (iso: string) => { const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

export default function ListingSchedulerPage() {
  const draftsQ = useQuery({
    queryKey: ["scheduled-drafts"],
    queryFn: async () => {
      const rows = await api<ScheduledDraft[]>("/api/listing-drafts?status=scheduled&light=1");
      return rows.filter((d) => d.scheduledAt).sort((a, b) => +new Date(a.scheduledAt!) - +new Date(b.scheduledAt!));
    },
  });
  const ids = (draftsQ.data ?? []).map((d) => d.id);
  const deadlinesQ = useQuery({
    queryKey: ["draft-deadlines", ids.join(",")],
    queryFn: () => api<Record<string, Deadline>>(`/api/trend-intelligence/draft-deadlines?ids=${ids.join(",")}`),
    enabled: ids.length > 0,
  });
  const legacyQ = useQuery({ queryKey: ["legacy-scheduled"], queryFn: () => api<LegacyScheduled[]>("/api/scheduled-listings") });

  const [editing, setEditing] = useState<string | null>(null);
  const [when, setWhen] = useState("");

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api(`/api/listing-drafts/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => { setEditing(null); void draftsQ.refetch(); },
  });
  const removeLegacy = useMutation({
    mutationFn: (id: string) => api(`/api/scheduled-listings/${id}`, { method: "DELETE" }),
    onSuccess: () => void legacyQ.refetch(),
  });

  const drafts = draftsQ.data ?? [];
  const legacy = (legacyQ.data ?? []).filter((l) => l.status === "pending");

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900"><Clock className="h-5 w-5 text-indigo-600" />Zamanlayıcı</h1>
        <p className="mt-1 text-sm text-gray-500">
          Planlı taslaklar, zamanı gelince otomatik olarak Etsy'de yayınlanır. Yeni plan için{" "}
          <Link to="/listing-drafts" className="text-indigo-700 hover:underline">Taslaklar</Link> ekranında listeleri seçip "Toplu Planla"yı kullanın.
        </p>
      </div>

      {(draftsQ.isError || patch.isError) && (
        <p className="mb-4 text-sm text-red-600">{((draftsQ.error || patch.error) as Error).message}</p>
      )}

      {draftsQ.isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : drafts.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">Planlı taslak yok.</div>
      ) : (
        <div className="space-y-2">
          {drafts.map((d) => {
            const dl = deadlinesQ.data?.[d.id];
            const late = dl?.deadline && d.scheduledAt && new Date(d.scheduledAt) > new Date(dl.deadline);
            const raw = d.imageUrls[0] || d.sourceImageUrl;
            const img = raw && !raw.startsWith("http") && !raw.startsWith("data:") ? `${getBackendUrl()}${raw}` : raw;
            return (
              <div key={d.id} className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                {img ? <img src={img} alt="" className="h-14 w-14 flex-shrink-0 rounded-lg border border-gray-200 object-cover" loading="lazy" />
                  : <div className="h-14 w-14 flex-shrink-0 rounded-lg bg-gray-100" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-900">{d.title}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-600">
                    <Calendar className="h-3.5 w-3.5" /> {fmt(d.scheduledAt!)}
                    {d.etsyListingId && <span className="text-gray-500"> · Etsy'de taslak olarak hazır</span>}
                  </p>
                  {dl && (
                    <p className={late ? "mt-1 flex items-center gap-1 text-xs text-amber-700" : "mt-1 text-xs text-gray-500"}>
                      {late && <AlertTriangle className="h-3.5 w-3.5" />}
                      Trend: {dl.eventTitle}
                      {dl.startsAt && ` · olay ${fmtDay(dl.startsAt)}`}
                      {dl.deadline && ` · son listeleme ${fmtDay(dl.deadline)}`}
                      {late && " (plan son tarihten sonra)"}
                    </p>
                  )}
                  {editing === d.id && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                      <button onClick={() => when && patch.mutate({ id: d.id, body: { status: "scheduled", scheduledAt: new Date(when).toISOString() } })}
                        disabled={!when || patch.isPending}
                        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">Kaydet</button>
                      <button onClick={() => setEditing(null)} className="rounded-lg px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100">Vazgeç</button>
                    </div>
                  )}
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  <button onClick={() => { setEditing(d.id); setWhen(toLocalInput(d.scheduledAt!)); }}
                    className="rounded-lg px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100">Saati değiştir</button>
                  <button onClick={() => { if (confirm("Plan iptal edilsin mi? Taslak silinmez, yalnız otomatik yayın kalkar.")) patch.mutate({ id: d.id, body: { status: "draft", scheduledAt: null } }); }}
                    className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" title="Planı iptal et" aria-label="Planı iptal et">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {legacy.length > 0 && (
        <div className="mt-8">
          <h2 className="text-sm font-semibold text-gray-700">Eski zamanlayıcı kayıtları ({legacy.length})</h2>
          <p className="mt-1 text-xs text-gray-500">
            Bu kayıtlar eski sistemden kaldı ve hiç otomatik yayınlanmadı. Yayınlanacaksa listeyi Taslaklar ekranından planlayın, sonra buradan silin.
          </p>
          <div className="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
            {legacy.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 px-4 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm text-gray-900">{l.title}</p>
                  <p className="text-xs text-gray-500">{fmt(l.scheduledAt)}</p>
                </div>
                <button onClick={() => { if (confirm("Eski kayıt silinsin mi?")) removeLegacy.mutate(l.id); }}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600" title="Sil" aria-label="Sil">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
